import React, { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';
import {
  genarateTableConfigs,
  generateGroupedTableConfigs,
  getGlobalSettings
} from '../utils';
import {
  TransactionDetalisPanel,
  TransactionDetalisPanelTitle,
  TransactionDetailsTable,
  SearchRow,
  MainFiltersRow,
  DownloadButtonWrapper
} from './styled';
import ComponentsMap from '../ComponentsMap';
import { DEFAULT_MAP_NAMESPACE } from './constants';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_PAGE_SIZE
} from '../../constants/commonConstants';
import {
  convertDateInputToEpoch,
  getTransactionExportZone,
  getTimeAdjustment
} from '../../../util/timezone';
import TransactionDetailsFilters from './TransactionDetailsFilters';
import TransactionSearchBar from './TransactionSearchBar';
import DownloadDropdown from './DownloadDropdown';
import AdvancedFiltersPanel from './AdvancedFiltersPanel';
import SortOrderToggle from './SortOrderToggle';
import {
  FRAML_CANONICAL_VIEW,
  framlCanonicalColumns,
  framlCanonicalSearchFields,
  canonicalTransactionKey,
  canonicalTransactionRowKey,
  FramlTransactionVolumes,
  FramlTransactionEvidence
} from './FramlCanonicalTransactions';

import { isEmpty, result } from '../../../util/lodashlike';
import { isFramlPSource } from '../../../util/framlP';
const MIN_SEARCH_LENGTH = 3;

// FIN-13743: Sender/Receiver ID and Name, after Transaction ID and Timestamp, so every Search-by
// option has a visible column.
const PARTY_COLUMN_KEYS = [
  'sender.key',
  'sender.name',
  'receiver.key',
  'receiver.name'
];
const withPartyColumns = (columns) => [
  ...columns.slice(0, 2),
  ...framlCanonicalColumns.filter((col) => PARTY_COLUMN_KEYS.includes(col.key)),
  ...columns.slice(2)
];

const buildSearchParams = (query) => {
  const params = {};
  if (query.searchValue && query.searchValue.length >= MIN_SEARCH_LENGTH) {
    params[query.searchField] = query.searchValue;
  }
  return params;
};

// Exported for FIN-12079 unit coverage — this is the ungated advanced-filter
// transform that carries transactionType/variantId/etc. into the list, download
// and pagination payloads.
export const buildAdvancedFilterParams = (
  query,
  { includeVariant = true } = {}
) => {
  const params = {};
  if (query.transactionType) params.transactionType = query.transactionType;
  if (query.accountId) params.accountId = query.accountId;
  // FIN-13748 — the canonical grids' column filters.
  if (query.senderAccount) params.senderAccount = query.senderAccount;
  if (query.receiverAccount) params.receiverAccount = query.receiverAccount;
  if (query.transactionTypeDescription)
    params.transactionTypeDescription = query.transactionTypeDescription;
  if (query.sortOrder) params.sortOrder = query.sortOrder;
  // FIN-12079 — variantId must ride the same ungated advanced-filter channel as
  // transactionType so the applied Variant reaches the request (and the async
  // download job's persisted filter_params) on list, download AND pagination.
  // Previously variantId sat solely behind the isFramlP ternary; this guarantees
  // it is always present when selected. 'all' sentinel → omit (true no-filter).
  if (includeVariant && query.variantId && query.variantId !== 'all') {
    params.variantId = query.variantId;
  }
  return params;
};

const TransactionDetails = ({
  apiActions,
  onDownloadComplete,
  alertId,
  isLoading,
  isCustomerInfoLoading,
  isAlertDetailsLoading,
  transactionHistory,
  settings,
  gsQuery,
  typologies,
  customerId,
  mapNamespace = DEFAULT_MAP_NAMESPACE,
  title,
  hasDownloadButton = true,
  hasTriggeredOnlySwitch = true,
  requireTriggeredOnlyParam,
  showWidgetActionLabels = true,
  className,
  alertDetails = {},
  hasTypologiesFilter = true,
  manualCaseDate,
  isSmartAssessment,
  transactionTriggeredVariants,
  isPrevention,
  isTxnHistoryDownloading,
  transactionTypesFilterData,
  isFromCM = false,
  moduleId,
  loginModuleId,
  isManualCase = false,
  module,
  caseId,
  // FIN-11398 — FRAML customer-detail mounts fetch by the customerId own-prop
  // (all of the customer's alerts, comma-joined alertId list), so the
  // single-alert `alertId === alertInfo.alertId` guard must not gate them.
  isCustomerScope = false
}) => {
  const { data: alertInfo } = alertDetails;
  // FIN-11542 — gate variant-vs-typology UI on FRAML-P source IDs (24/25/29)
  const isFramlP = isFramlPSource(alertInfo?.alertSourceId);
  // The server opts in each validated client; legacy responses retain their existing columns.
  const isCanonicalCustomer =
    isCustomerScope && transactionHistory?.view === FRAML_CANONICAL_VIEW;
  // FIN-13743: the single-alert FRAML-P read (/fraudAlerts) returns the same role-shaped rows, so it
  // gets the Sender/Receiver Search-by and columns while keeping the rest of the alert grid.
  const isCanonicalAlert =
    !isCustomerScope && transactionHistory?.view === FRAML_CANONICAL_VIEW;
  // FIN-13688: every fetch nulls transactionHistory while it loads. Keep the Search-by options of
  // the last loaded response so an active Sender/Receiver search is not reset mid-request.
  const [hasCanonicalSearch, setHasCanonicalSearch] = useState(false);
  useEffect(() => {
    if (transactionHistory)
      setHasCanonicalSearch(isCanonicalCustomer || isCanonicalAlert);
  }, [transactionHistory, isCanonicalCustomer, isCanonicalAlert]);
  const triggeredOnlyParam = (value) =>
    isCanonicalCustomer
      ? Boolean(value || requireTriggeredOnlyParam)
      : value || requireTriggeredOnlyParam;
  const evidenceByTransaction = useMemo(() => {
    const grouped = new Map();
    if (isCanonicalCustomer && Array.isArray(transactionHistory?.evidence)) {
      transactionHistory.evidence.forEach((item) => {
        const key = canonicalTransactionKey({
          id: item.transactionId,
          transactionDate: item.transactionDate
        });
        if (!grouped.has(key)) grouped.set(key, []);
        grouped.get(key).push(item);
      });
    }
    return grouped;
  }, [isCanonicalCustomer, transactionHistory?.evidence]);
  const evidenceForRow = (row) =>
    evidenceByTransaction.get(canonicalTransactionKey(row)) || [];
  const [currentPage, setCurrentPage] = useState(DEFAULT_CURRENT_PAGE);
  const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [query, setQuery] = useState({
    isTriggeredOnly: true,
    typologyId: 'all',
    variantId: undefined,
    duration: 30,
    searchField: '',
    searchValue: '',
    transactionType: undefined,
    accountId: undefined,
    senderAccount: undefined,
    receiverAccount: undefined,
    transactionTypeDescription: undefined,
    sortOrder: 'DESC'
  });
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);

  const alertDateTO = useMemo(() => {
    if (manualCaseDate) return moment(new Date(manualCaseDate), 'DD/MM/YYYY');
    if (alertInfo?.alertDate) {
      const { hours = '' } = getTimeAdjustment();
      if (hours)
        return moment(new Date(alertInfo?.alertDate), 'DD/MM/YYYY').add(
          hours,
          'hours'
        );

      return moment(new Date(alertInfo?.alertDate), 'DD/MM/YYYY');
    }

    return moment();
  }, [manualCaseDate, alertInfo]);

  const alertDateFROM = useMemo(
    () =>
      moment(alertDateTO)
        .subtract(query.duration ?? 30, 'days')
        .add(1, 'days'),
    [alertDateTO]
  );

  const {
    getAMLSCommonAlertPartyIdentity,
    getAMLSTMCommonTransactionHistory,
    getAMLSTMCommonTransactionTriggeredTypologies,
    getAMLSTMCommonTransactionTriggeredVariants,
    downloadAMLSTMCommonTransactionHistory,
    getAMLSTMCommonTransactionTypesFilter
  } = apiActions;
  const {
    widgetConfig,
    isSingleLevel = false,
    isVisible
  } = getGlobalSettings({ settings, gsQuery }) || {};

  const getAMLSTMCommonTransactionHistoryFunc = (
    from,
    to,
    triggeredOnly,
    typologyId,
    customerId
  ) => {
    const searchParams = buildSearchParams(query);
    // FIN-13578 — the customer tab now filters by variant, so it rides the same ungated
    // advanced-filter channel (FIN-12079) as every other FRAML-P surface.
    const enhancedFilterParams = buildAdvancedFilterParams(query);

    return getAMLSTMCommonTransactionHistory({
      customerId: customerId ?? undefined,
      ...(module && { module }),
      alertId,
      limit: currentPageSize,
      offset: (DEFAULT_CURRENT_PAGE - 1) * currentPageSize,
      q: '',
      from: convertDateInputToEpoch(
        from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        true
      ),
      to: convertDateInputToEpoch(
        to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
        true
      ),
      isTriggeredOnly: triggeredOnlyParam(triggeredOnly),
      // FIN-12659 — single-alert FRAML-P reads go to the alert-scoped fraud
      // endpoint (no JSON envelope); customer-scope mounts (comma-joined
      // alertId list, FIN-11398) stay on the customer endpoint.
      isFraudAlertScope: isFramlP && !isCustomerScope,
      // FIN-11542 — FRAML-P sends variantId; non-FRAML-P sends typologyId.
      // FIN-13578 — the canonical customer tab joins the FRAML-P side: Fraud Prevention filters
      // by variant, not typology. The BE guard that used to reject variantId there is gone now
      // that triggered_rule is part of the triggered_transactions PRIMARY KEY.
      ...(isFramlP || isCanonicalCustomer
        ? { variantId: query.variantId === 'all' ? undefined : query.variantId }
        : { typologyId: typologyId === 'all' ? undefined : typologyId }),
      ...searchParams,
      ...enhancedFilterParams
    });
  };

  useEffect(() => {
    // Skip date calculation and query updates while alert details are still loading
    // This prevents unnecessary state updates
    // For manual cases, alertDetails is not fetched, so skip this guard
    if (isAlertDetailsLoading && !isManualCase) return;

    //The "To" date should not greater than alert Date.
    //So compare the "To" with "Alert Date" when date range only selected(without duration)
    if (query.from && query.to && !query.duration) {
      if (alertDateTO.endOf('day').isBefore(query.to.endOf('day'))) {
        setQuery((value) => ({
          ...value,
          from: alertDateFROM,
          to: alertDateTO,
          duration: 30
        }));
      }
    } else {
      setQuery((value) => ({
        ...value,
        from: alertDateFROM,
        to: alertDateTO,
        duration: value.duration
      }));
    }
  }, [alertDateTO]);

  useEffect(() => {
    // Set the typologyId as 'all' when the alert doesn't contain the selected typology while switching alert.
    if (!isCanonicalCustomer && query?.typologyId !== 'all') {
      if (
        (!isPrevention &&
          typologies?.length &&
          !typologies?.some(({ id }) => id === query?.typologyId)) ||
        (transactionTriggeredVariants?.length &&
          !transactionTriggeredVariants?.some((id) => id === query?.typologyId))
      ) {
        setQuery((value) => ({
          ...value,
          typologyId: 'all'
        }));
      }
    }
    // FIN-11542 — reset variantId when the offered variant list no longer contains it.
    // FIN-13578 — validate against the list that actually drives the dropdown. On the canonical
    // customer tab that is the server's variantNames; `transactionTriggeredVariants` is the
    // alert-scoped list, which this tab never populates (no alertId) but which Redux can still
    // hold from a previously visited alert page. Validating against that stale list silently
    // cleared a legitimately selected variant.
    const offeredVariants = isCanonicalCustomer
      ? transactionHistory?.variantNames
      : transactionTriggeredVariants;
    if (query?.variantId && query.variantId !== 'all') {
      if (
        offeredVariants?.length &&
        !offeredVariants?.some((v) => v === query.variantId)
      ) {
        setQuery((value) => ({ ...value, variantId: undefined }));
      }
    }
  }, [
    typologies,
    transactionTriggeredVariants,
    isCanonicalCustomer,
    transactionHistory?.variantNames,
    query?.typologyId,
    query?.variantId
  ]);

  useEffect(() => {
    const { from, to, typologyId, isTriggeredOnly } = query;
    if (
      (isVisible || isManualCase) &&
      from &&
      (manualCaseDate || isCustomerScope || alertId === alertInfo?.alertId)
    ) {
      const triggeredOnly = hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;
      if (customerId) {
        getAMLSTMCommonTransactionHistoryFunc(
          from,
          to,
          triggeredOnly,
          typologyId,
          customerId
        );
      }
    }
  }, [customerId, query, isVisible, alertInfo]);

  useEffect(() => {
    if (isManualCase && alertId) {
      getAMLSCommonAlertPartyIdentity({ alertId });
    }
  }, [alertId, isManualCase]);

  useEffect(() => {
    if (hasTypologiesFilter && alertId) {
      if (isPrevention) {
        getAMLSTMCommonTransactionTriggeredVariants({
          alertId,
          eventType: 'transaction'
        });
      } else {
        getAMLSTMCommonTransactionTriggeredTypologies({ alertId });
      }
    }
  }, [alertId, hasTypologiesFilter]);

  useEffect(() => {
    if (
      customerId &&
      getAMLSTMCommonTransactionTypesFilter &&
      !isCanonicalCustomer
    ) {
      getAMLSTMCommonTransactionTypesFilter({
        partyKey: customerId
      });
    }
  }, [customerId, isCanonicalCustomer]);

  const onChangeWidgetAction = (values) => {
    const triggeredOnly = hasTriggeredOnlySwitch
      ? values.isTriggeredOnly
      : undefined;

    setQuery((prev) => ({
      ...prev,
      ...values,
      // FIN-13578 — the canonical customer tab filters by variant instead of typology, so the
      // clear-on-toggle-off behaviour carries over to variantId.
      // A variant is only meaningful over the triggered set: with Triggered Only off the grid
      // starts from all of the customer's history, where "which rule fired this" has no meaning.
      // AdvancedFiltersPanel already disables both the Variants and Typologies dropdowns on
      // `!isTriggeredOnly`; clearing here keeps a stale selection from riding along invisibly in
      // the list, pagination and export requests while the control that set it is greyed out.
      ...(isCanonicalCustomer && values.isTriggeredOnly === false
        ? { typologyId: 'all', variantId: undefined }
        : {}),
      isTriggeredOnly: triggeredOnlyParam(triggeredOnly)
    }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  };

  const onSearchChange = useCallback(({ searchField, searchValue }) => {
    setQuery((prev) => ({ ...prev, searchField, searchValue }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  const onAdvancedFiltersApply = useCallback((advancedValues) => {
    setQuery((prev) => ({ ...prev, ...advancedValues }));
    setCurrentPage(DEFAULT_CURRENT_PAGE);
  }, []);

  const handleDownload = ({ downloadAll = false } = {}) => {
    // FIN-12984 / FIN-12936 — identical defect to the Activity tab: the FRAML customer-detail
    // mount passes alertId={undefined}, so this guard blocked the download dispatch entirely and
    // the customer-scoped Transaction export produced nothing. Fixed here in the same change
    // because FIN-12936 would otherwise fail re-test for exactly this reason.
    if (
      (!isEmpty(alertId) || isCustomerScope) &&
      isVisible &&
      transactionHistory?.payload?.length
    ) {
      const { from, to, typologyId, isTriggeredOnly } = query;

      const triggeredOnly = hasTriggeredOnlySwitch
        ? isTriggeredOnly
        : undefined;
      const searchParams = buildSearchParams(query);
      // FIN-13578 — the canonical customer tab filters by variant now, on every path.
      const enhancedFilterParams = buildAdvancedFilterParams(query);

      const downloadParams = {
        customerId,
        alertId,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly: triggeredOnlyParam(triggeredOnly),
        // FIN-11542 — FRAML-P sends variantId; non-FRAML-P sends typologyId.
        // FIN-13578 — the canonical customer tab joins the FRAML-P side.
        ...(isFramlP || isCanonicalCustomer
          ? {
              variantId: query.variantId === 'all' ? undefined : query.variantId
            }
          : { typologyId: typologyId === 'all' ? undefined : typologyId }),
        download: true,
        moduleId: alertInfo?.alertSourceId
          ? Number(alertInfo.alertSourceId)
          : moduleId ?? loginModuleId,
        ...(!downloadAll && {
          offset: (currentPage - 1) * currentPageSize,
          limit: currentPageSize
        }),
        ...(downloadAll && { downloadAll: true, isCM: isFromCM }),
        ...searchParams,
        ...enhancedFilterParams,
        ...(module && { module }),
        // FIN-12993 / FIN-13996: export transaction dates in the zone the grid renders them in
        // (viewer zone + tenant time-adjustment); 'utc' download mode keeps the raw stored value
        zoneId: getTransactionExportZone(),
        // FIN-13443 — Download History refresh hook, invoked by the saga on
        // submission success (mirrors the Activity tab).
        onSuccess: onDownloadComplete
      };

      // Include caseId for all CM_CASE_* and manual case modules
      const isCaseModule = isManualCase || module?.startsWith('CM_CASE_');
      if (isCaseModule && caseId) {
        downloadParams.caseId = caseId;
      }

      downloadAMLSTMCommonTransactionHistory(downloadParams);
    }
  };

  const onDownloadClick = () => handleDownload();
  const onDownloadAllClick = () => handleDownload({ downloadAll: true });

  const onPaginationChange = (page, pageSize) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
    const { from, to, isTriggeredOnly, typologyId } = query;
    // FIN-12984 / FIN-12936 — same relaxation on the pagination handler.
    if (from && to && (!isEmpty(alertId) || isCustomerScope) && isVisible) {
      const searchParams = buildSearchParams(query);
      // FIN-13578 — the canonical customer tab filters by variant now, on every path.
      const enhancedFilterParams = buildAdvancedFilterParams(query);

      getAMLSTMCommonTransactionHistory({
        customerId,
        ...(module && { module }),
        alertId,
        limit: pageSize,
        offset: (page - 1) * pageSize,
        q: '',
        from: convertDateInputToEpoch(
          from.startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        to: convertDateInputToEpoch(
          to.endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
          true
        ),
        isTriggeredOnly,
        // FIN-12659 — same fraud-endpoint routing as the initial fetch.
        isFraudAlertScope: isFramlP && !isCustomerScope,
        // FIN-11542 — FRAML-P sends variantId; non-FRAML-P sends typologyId.
        // FIN-13578 — the canonical customer tab joins the FRAML-P side.
        ...(isFramlP || isCanonicalCustomer
          ? {
              variantId: query.variantId === 'all' ? undefined : query.variantId
            }
          : { typologyId: typologyId === 'all' ? undefined : typologyId }),
        isSmartAssessment,
        ...searchParams,
        ...enhancedFilterParams
      });
    }
  };

  const typologyVariants = useMemo(() => {
    if (!Array.isArray(transactionTriggeredVariants)) return [];

    return [...new Set(transactionTriggeredVariants)].map((d) => ({
      id: d,
      name: d
    }));
  }, [transactionTriggeredVariants]);

  const legacyColumns = () =>
    (isSingleLevel ? genarateTableConfigs : generateGroupedTableConfigs)({
      settings: widgetConfig,
      configs: result(ComponentsMap, mapNamespace) || {}
    });

  // FIN-13578 — Variant options on the FRAML customer tab.
  // The alert-scoped fetch above is gated on `alertId`, and TransactionDetailsAdapter passes
  // alertId={undefined} on purpose (FIN-12905: the comma-joined id list blew the URI limit), so
  // `transactionTriggeredVariants` is always empty here. The canonical customer response carries
  // its own list instead, read from triggered_transactions — the same table the grid filters on,
  // so every offered variant can actually return a row. Sourcing it from
  // unified_alerts.TRIGGERED_RULES would re-offer activity-only variants that never match a
  // transaction, which is the "No data" the analyst hit.
  // FIN-13747 — the single-alert FRAML-P read (/fraudAlerts) returns the facet scoped like the
  // grid (Triggered Only, window); the party-wide list ignores both, so it is only the fallback.
  const transactionTypeCodes = Array.isArray(
    transactionHistory?.transactionTypes
  )
    ? transactionHistory.transactionTypes
    : transactionTypesFilterData || [];

  // FIN-13748 — on the canonical grids every Advanced Filter acts on a visible column. The customer
  // grid shows Sender and Receiver Account. The alert grid keeps the classic columns, where
  // Account Number is the sender's account and Transaction Type shows the description, next to
  // Transaction Type Code. While a fetch is in flight the last loaded view holds, like Search-by.
  const isCanonicalView = transactionHistory
    ? isCanonicalCustomer || isCanonicalAlert
    : hasCanonicalSearch;
  const canonicalColumnFilters = !isCanonicalView
    ? undefined
    : isCustomerScope
    ? [
        { key: 'senderAccount', label: 'Sender Account' },
        { key: 'receiverAccount', label: 'Receiver Account' }
      ]
    : [
        { key: 'senderAccount', label: 'Account Number' },
        {
          key: 'transactionTypeDescription',
          label: 'Transaction Type',
          options: transactionHistory?.transactionTypeDescriptions || []
        },
        {
          key: 'transactionType',
          label: 'Transaction Type Code',
          options: transactionTypeCodes
        }
      ];

  const variantOptions = useMemo(() => {
    if (!isCanonicalCustomer) return typologyVariants;
    const names = transactionHistory?.variantNames;
    if (!Array.isArray(names)) return [];
    return [...new Set(names)].map((d) => ({ id: d, name: d }));
  }, [isCanonicalCustomer, typologyVariants, transactionHistory]);

  return (
    <TransactionDetalisPanel className={className}>
      {title && (
        <TransactionDetalisPanelTitle style={{ padding: '12px 16px 0' }}>
          {title}
        </TransactionDetalisPanelTitle>
      )}

      <SearchRow>
        <TransactionSearchBar
          onSearch={onSearchChange}
          advancedFiltersOpen={advancedFiltersOpen}
          onToggleAdvancedFilters={setAdvancedFiltersOpen}
          isTMTransaction={!isManualCase}
          searchFields={
            hasCanonicalSearch ? framlCanonicalSearchFields : undefined
          }
          advancedFiltersOverlay={
            <AdvancedFiltersPanel
              filters={query}
              onApply={(values) => {
                onAdvancedFiltersApply(values);
                setAdvancedFiltersOpen(false);
              }}
              isTriggeredOnly={query.isTriggeredOnly}
              onClose={() => setAdvancedFiltersOpen(false)}
              typologies={
                isCanonicalCustomer
                  ? (transactionHistory?.typologyIds || []).map((id) => ({
                      id,
                      name: String(id)
                    }))
                  : isPrevention && !isFramlP
                  ? typologyVariants
                  : typologies || []
              }
              transactionTypes={transactionTypeCodes}
              columnFilters={canonicalColumnFilters}
              isTMTransaction={!isManualCase}
              isFramlP={isFramlP || isCanonicalCustomer}
              variants={variantOptions}
              variantId={query.variantId}
            />
          }
        />
      </SearchRow>

      <MainFiltersRow>
        <TransactionDetailsFilters
          hasTriggeredOnlySwitch={hasTriggeredOnlySwitch}
          showLabels={showWidgetActionLabels}
          filters={query}
          onFilterChange={onChangeWidgetAction}
          alertDateTO={alertDateTO}
        />
        <SortOrderToggle
          label="Sort Transaction Date:"
          value={query.sortOrder || 'DESC'}
          onChange={(order) => {
            setCurrentPage(DEFAULT_CURRENT_PAGE);
            setQuery((prev) => ({ ...prev, sortOrder: order }));
          }}
        />
        {hasDownloadButton && (
          <DownloadButtonWrapper>
            <DownloadDropdown
              onDownloadCurrentPage={onDownloadClick}
              onDownloadAll={onDownloadAllClick}
              isDownloading={isTxnHistoryDownloading}
              isDisabled={
                (transactionHistory?.payload || []).length === 0 || isLoading
              }
              currentPageCount={(transactionHistory?.payload || []).length}
              totalCount={transactionHistory?.total || 0}
            />
          </DownloadButtonWrapper>
        )}
      </MainFiltersRow>

      {isCanonicalCustomer && (
        <FramlTransactionVolumes volumes={transactionHistory?.volumes || []} />
      )}

      <TransactionDetailsTable
        dataSource={transactionHistory?.payload || []}
        {...(isCanonicalCustomer && {
          rowKey: canonicalTransactionRowKey,
          expandable: {
            showExpandColumn: (transactionHistory?.payload || []).some(
              (row) => evidenceForRow(row).length > 0
            ),
            rowExpandable: (row) => evidenceForRow(row).length > 0,
            expandedRowRender: (row) => (
              <FramlTransactionEvidence
                evidence={evidenceForRow(row)}
                row={row}
              />
            )
          }
        })}
        columns={
          isCanonicalCustomer
            ? framlCanonicalColumns
            : isCanonicalAlert
            ? withPartyColumns(legacyColumns())
            : legacyColumns()
        }
        loading={isLoading}
        scroll={{ x: 'max-content' }}
        pagination={{
          total: transactionHistory?.total || 0,
          current: currentPage,
          onChange: onPaginationChange,
          pageSize: currentPageSize,
          showTotal: (total) => `${total} Records`,
          showSizeChanger: false
        }}
      />
    </TransactionDetalisPanel>
  );
};

TransactionDetails.propTypes = {
  apiActions: PropTypes.object.isRequired,
  onDownloadComplete: PropTypes.func,
  alertId: PropTypes.string,
  transactionHistory: PropTypes.object,
  transactionHistoryDownloaded: PropTypes.object,
  error: PropTypes.object,
  isLoading: PropTypes.bool,
  isCustomerInfoLoading: PropTypes.bool,
  isAlertDetailsLoading: PropTypes.bool,
  settings: PropTypes.object.isRequired,
  gsQuery: PropTypes.object.isRequired,
  typologies: PropTypes.array,
  customerId: PropTypes.string.isRequired,
  mapNamespace: PropTypes.string,
  hasDownloadButton: PropTypes.bool,
  title: PropTypes.string,
  hasTriggeredOnlySwitch: PropTypes.bool,
  requireTriggeredOnlyParam: PropTypes.bool,
  showWidgetActionLabels: PropTypes.bool,
  className: PropTypes.string,
  csvHeaders: PropTypes.object,
  hasTypologiesFilter: PropTypes.bool,
  alertDetails: PropTypes.object,
  manualCaseDate: PropTypes.string,
  isPrevention: PropTypes.bool,
  isTxnHistoryDownloading: PropTypes.bool,
  transactionTypesFilterData: PropTypes.array,
  isManualCase: PropTypes.bool,
  isCustomerScope: PropTypes.bool
};

export default TransactionDetails;
